import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash, FloppyDisk, FilePdf, Export, Rows, UploadSimple,
  CheckSquare, Square, Copy, FolderPlus, DotsSixVertical,
  CaretDown, CaretRight, MapPin, MagicWand, FileText, ListChecks, ChatCircleDots, ClockCounterClockwise,
} from '@phosphor-icons/react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import DPGFAIIcon from './DPGFAIIcon';
import DPGFProactiveBanner from './DPGFProactiveBanner';
import DpgfDceConsistencyBanner from './DpgfDceConsistencyBanner';
import DPGFGenerateAIDialog from './DPGFGenerateAIDialog';
import ImporterDialog from './ImporterDialog';
import { BOUTONS } from '../../lib/libelles';
import DPGFAvenantsManager from './DPGFAvenantsManager';
import DPGFLocationPopover from './DPGFLocationPopover';
import AmoCommentBox from './AmoCommentBox';
import DPGFVersionsModal from './DPGFVersionsModal';
import usePermissions from '../../hooks/usePermissions';
import useLotsConfies, { peutEcrireLot } from '../../hooks/useLotsConfies';
import LotConfieBar from './LotConfieBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const UNCATEGORIZED = '__sans_categorie__';

function genId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function InlineCell({ value, onChange, type = 'text', placeholder = '', className = '', readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(value); }, [value]);

  const commit = () => {
    setEditing(false);
    const v = type === 'number' ? parseFloat(localVal) || 0 : localVal;
    if (v !== value) onChange(v);
  };

  if (editing && !readOnly) {
    return (
      <input
        ref={inputRef}
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        value={localVal}
        onChange={e => setLocalVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setLocalVal(value); setEditing(false); } }}
        className={`w-full bg-white border border-[#C75B39] px-2 py-1 text-sm outline-none ${className}`}
        autoFocus
      />
    );
  }

  return (
    <div
      onClick={() => { if (!readOnly) setEditing(true); }}
      className={`px-2 py-1 text-sm min-h-[30px] border border-transparent transition-colors ${readOnly ? '' : 'cursor-text hover:bg-[#F5F5F3] hover:border-[#E0E0DB]'} ${className} ${!value && value !== 0 ? 'text-[#9A9A9A] italic' : ''}`}
    >
      {type === 'number' ? (value || 0).toLocaleString('fr-FR', { minimumFractionDigits: value % 1 !== 0 ? 2 : 0 }) : (value || placeholder)}
    </div>
  );
}

// ============================================================
// SortablePoste — une ligne poste draggable
// ============================================================
function SortablePoste({
  poste, lotId, lotName, lotNum, posteNum, isSelected, canEdit, canEditCctp, chantierId, nbTaches,
  onToggle, onUpdate, onDelete, onOpenLocation, onCommentAmo,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `poste:${poste.id}`,
    data: { type: 'poste', poste },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const total = (poste.quantite || 0) * (poste.prix_unitaire || 0);
  const hasLocation = !!(poste.localisation_texte || poste.piece_id);

  return (
    <div
      ref={setNodeRef} style={style}
      className={`grid grid-cols-[24px_32px_40px_1fr_80px_90px_120px_120px_36px] border-b border-[#F0F0ED] items-center hover:bg-[#FAFAF8] group ${isSelected ? 'bg-[#FFF5F2]' : ''}`}
      data-testid={`dpgf-poste-${poste.id}`}
    >
      <span
        {...(canEdit ? { ...attributes, ...listeners } : {})}
        className={`p-2 flex items-center justify-center text-[#C0C0C0] ${canEdit ? 'hover:text-[#6B6B6B] cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'} touch-none select-none`}
        title={canEdit ? 'Glisser pour réordonner' : 'Lecture seule'}
        data-testid={`dpgf-poste-handle-${poste.id}`}
        role="button"
        tabIndex={canEdit ? 0 : -1}
      >
        <DotsSixVertical size={14} />
      </span>
      <div className="p-2 flex items-center justify-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="h-3 w-3 accent-[#C75B39] cursor-pointer"
          data-testid={`dpgf-poste-checkbox-${poste.id}`}
        />
      </div>
      <div className="px-2 text-xs text-[#9A9A9A]" data-testid={`dpgf-poste-num-${poste.id}`}>{lotNum}.{posteNum}</div>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1 group">
          <div className="flex-1 min-w-0">
            <InlineCell value={poste.designation} onChange={v => onUpdate('designation', v)} placeholder="Désignation du poste" readOnly={!canEdit} />
          </div>
          {poste.suggere_ia && (
            <span className="text-[9px] text-[#6366F1] bg-[#6366F1]/10 px-1 py-0.5 flex-shrink-0 whitespace-nowrap">IA</span>
          )}
          <button
            onClick={onOpenLocation}
            className={`flex-shrink-0 p-1 transition-colors ${hasLocation ? 'text-[#C75B39]' : 'text-[#C0C0C0] hover:text-[#C75B39]'}`}
            title={hasLocation ? `Localisation : ${poste.localisation_texte || 'pièce rattachée'}` : 'Rattacher à une pièce / plan'}
            disabled={!canEdit}
            data-testid={`dpgf-poste-location-btn-${poste.id}`}
          >
            <MapPin size={14} weight={hasLocation ? 'fill' : 'regular'} />
          </button>
          {canEdit && (
            <DPGFAIIcon
              chantierId={chantierId}
              designation={poste.designation}
              lotId={lotId}
              posteId={poste.id}
              lotName={lotName}
              unite={poste.unite}
              quantite={poste.quantite}
              canGenerateCCTP={canEditCctp}
              onUpdateDesignation={v => onUpdate('designation', v)}
            />
          )}
          {onCommentAmo && (
            <button
              onClick={onCommentAmo}
              className="flex-shrink-0 p-1 text-[#C0C0C0] hover:text-[#7B5EA7] transition-colors"
              title="Commenter ce poste (AMO)"
              data-testid={`dpgf-poste-amo-comment-${poste.id}`}
            >
              <ChatCircleDots size={14} />
            </button>
          )}
        </div>
        {hasLocation && (
          <div className="text-[10px] text-[#C75B39] italic flex items-center gap-1 px-2 -mt-0.5" data-testid={`dpgf-poste-location-badge-${poste.id}`}>
            <MapPin size={9} weight="fill" /> {poste.localisation_texte || '(pièce rattachée)'}
          </div>
        )}
        {nbTaches > 0 && (
          <div
            className="text-[10px] text-[#1E3A5F] flex items-center gap-1 px-2 -mt-0.5 font-bold"
            data-testid={`dpgf-poste-nb-taches-${poste.id}`}
            title={`${nbTaches} action${nbTaches > 1 ? 's' : ''} rattachée${nbTaches > 1 ? 's' : ''} à ce poste`}
          >
            <ListChecks size={10} weight="bold" /> {nbTaches} action{nbTaches > 1 ? 's' : ''} rattachée{nbTaches > 1 ? 's' : ''}
          </div>
        )}
      </div>
      <InlineCell value={poste.unite} onChange={v => onUpdate('unite', v)} placeholder="m²" readOnly={!canEdit} />
      <InlineCell value={poste.quantite} onChange={v => onUpdate('quantite', v)} type="number" className="text-right" readOnly={!canEdit} />
      <InlineCell value={poste.prix_unitaire} onChange={v => onUpdate('prix_unitaire', v)} type="number" className="text-right" readOnly={!canEdit} />
      <div className="px-2 py-1 text-sm text-right font-medium text-[#2C2C2C]">{total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
      <div className="flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        {canEdit && (
          <button onClick={onDelete} className="text-[#9A9A9A] hover:text-[#8B3A3A]" data-testid={`delete-poste-${poste.id}`}>
            <Trash size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SortableLot — un lot draggable
// ============================================================
function SortableLot({ lot, lotIdx, children, onRemove, onRename, isFullySelected, isPartiallySelected, onToggleAll, lotTotal, canEdit, canEditStructure = canEdit, onNavigateCCTP, onUpdateLotTva, tvaDefaut }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `lot:${lot.id}`,
    data: { type: 'lot', lot },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`dpgf-lot-${lot.id}`}>
      {/* Lot Header */}
      <div className="grid grid-cols-[24px_32px_40px_1fr_80px_90px_120px_120px_36px] bg-[#C75B39] text-white items-center group">
        <span
          {...(canEditStructure ? { ...attributes, ...listeners } : {})}
          className={`p-2 flex items-center justify-center text-white/60 ${canEditStructure ? 'hover:text-white cursor-grab active:cursor-grabbing' : 'opacity-40 cursor-not-allowed'} touch-none select-none`}
          title={canEditStructure ? 'Glisser le lot' : 'Lecture seule'}
          data-testid={`dpgf-lot-handle-${lot.id}`}
          role="button"
          tabIndex={canEditStructure ? 0 : -1}
        >
          <DotsSixVertical size={14} />
        </span>
        <div className="p-2 flex items-center justify-center">
          <input
            type="checkbox"
            checked={isFullySelected}
            ref={(el) => { if (el) el.indeterminate = isPartiallySelected; }}
            onChange={onToggleAll}
            disabled={!lot.postes || lot.postes.length === 0}
            className="h-3 w-3 accent-white cursor-pointer disabled:opacity-40"
            data-testid={`dpgf-lot-checkbox-${lot.id}`}
          />
        </div>
        <div className="p-2 text-xs font-bold">LOT {String(lotIdx + 1).padStart(2, '0')}</div>
        <div className="p-1 col-span-4 flex items-center gap-2">
          <input
            value={lot.nom}
            onChange={e => onRename(e.target.value)}
            readOnly={!canEdit}
            className="bg-transparent text-white text-sm font-bold flex-1 outline-none placeholder-white/50"
            placeholder="Nom du lot"
            data-testid={`dpgf-lot-name-${lot.id}`}
          />
          {/* Iter 84 — Override TVA par lot */}
          <div className="flex items-center gap-1 bg-white/10 border border-white/30 px-1.5 py-0.5 text-[10px]" title="TVA spécifique à ce lot (sinon hérite du DPGF)">
            <span className="text-white/70">TVA</span>
            <select
              value={lot.tva_pct == null ? '' : String(lot.tva_pct)}
              onChange={(e) => onUpdateLotTva && onUpdateLotTva(lotIdx, e.target.value === '' ? null : parseFloat(e.target.value))}
              disabled={!canEdit}
              className="bg-transparent text-white text-[10px] font-semibold outline-none cursor-pointer disabled:cursor-not-allowed"
              data-testid={`lot-tva-select-${lot.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="" className="text-black">Défaut ({tvaDefaut}%)</option>
              <option value="5.5" className="text-black">5,5%</option>
              <option value="10" className="text-black">10%</option>
              <option value="20" className="text-black">20%</option>
            </select>
          </div>
        </div>
        <div className="p-2 text-right text-sm font-bold">{lotTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
        <div className="p-1 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {onNavigateCCTP && (
            <button onClick={(e) => { e.stopPropagation(); onNavigateCCTP(lot.id); }} className="text-white/70 hover:text-white" data-testid={`dpgf-lot-cctp-link-${lot.id}`} title="Voir le CCTP de ce lot">
              <FileText size={14} />
            </button>
          )}
          {canEditStructure && (
            <button onClick={onRemove} className="text-white/70 hover:text-white" data-testid={`delete-lot-${lot.id}`}>
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function DPGFEditor({ chantierId, chantierName, onBack, onNavigateCCTP }) {
  const { perms, role_projet } = usePermissions(chantierId);
  // RP44-45 — délégation par lot : `canEdit` = écriture de TOUTE la DPGF (lots, import,
  // TVA par défaut…) ; `canEditLot(id)` = écriture d'un lot (lots confiés à la personne,
  // ou tous sauf ceux confiés à d'autres). Le serveur applique la même règle.
  const lotsConfies = useLotsConfies(chantierId);
  const porteeDpgf = lotsConfies.data.moi.dpgf;
  const canEdit = !!perms?.can_modify_dpgf && !!porteeDpgf.complete;
  const canEditLot = (lotId) => peutEcrireLot(porteeDpgf, lotId);
  const canEditAny = canEdit || porteeDpgf.mode !== 'aucun';
  const refreshLotsRef = useRef(null);
  refreshLotsRef.current = lotsConfies.refresh;
  const canEditCctp = !!perms?.can_modify_cctp; // Audit 2026-09 CON-07
  const canCommentAmo = ['moe_principal', 'moe_membre'].includes(role_projet); // Sprint C
  const editTitle = canEdit ? undefined : 'Lecture seule : modification du DPGF réservée à l\'architecte (ou droit confié). Demandez à l\'architecte.';
  const [lots, setLots] = useState([]);
  const [tvaDefaut, setTvaDefaut] = useState(20);  // TVA Sprint — Iter 79
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [importerOpen, setImporterOpen] = useState(false);
  const [selectedPostes, setSelectedPostes] = useState(new Set()); // contient des keys "lotIdx-posteIdx"
  const [collapsedCats, setCollapsedCats] = useState(new Set()); // "lotIdx::categorie"
  const [dpgfStatus, setDpgfStatus] = useState('BROUILLON');
  const [improving, setImproving] = useState(false);
  const [locationFor, setLocationFor] = useState(null); // { lotIdx, posteIdx }
  const [tasksByPoste, setTasksByPoste] = useState({}); // Sprint B — { poste_id: nb_taches }
  // Sprint C — Commentaires AMO sur DPGF
  const [amoCommentBox, setAmoCommentBox] = useState(null); // { posteId, posteDesignation, lotId, lotNom, defaultType }
  const [amoComments, setAmoComments] = useState([]); // commentaires non résolus
  // Sprint D — Modal versions historiques
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const fileInputRef = useRef(null);
  const saveTimeout = useRef(null);
  const consistencyBannerRef = useRef(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { fetchDPGF(); fetchTasksCount(); fetchAmoComments(); /* eslint-disable-next-line */ }, [chantierId]);

  const fetchDPGF = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/dpgf`, { withCredentials: true });
      setLots(data.lots || []);
      setDpgfStatus(data.statut || 'BROUILLON');
      setTvaDefaut(typeof data.tva_defaut === 'number' ? data.tva_defaut : 20);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Sprint B — Compteurs tâches rattachées par poste
  const fetchTasksCount = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/lignes-disponibles`,
        { withCredentials: true },
      );
      const map = {};
      (data.lots || []).forEach((l) => (l.postes || []).forEach((p) => {
        if (p.nb_taches > 0) map[p.poste_id] = p.nb_taches;
      }));
      setTasksByPoste(map);
    } catch (e) {
      // silent — c'est juste un compteur d'aide
    }
  };

  // Sprint C — Commentaires AMO sur DPGF (non résolus uniquement pour le bandeau)
  const fetchAmoComments = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/commentaires`,
        { params: { resolu: false }, withCredentials: true },
      );
      setAmoComments(Array.isArray(data) ? data : []);
    } catch (e) {
      setAmoComments([]);
    }
  };

  const markAmoCommentResolu = async (commentId) => {
    try {
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/commentaire/${commentId}/resolu`,
        {},
        { withCredentials: true },
      );
      toast.success('Commentaire marqué traité');
      fetchAmoComments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  const autoSave = useCallback((newLots, newTvaDefaut = null) => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      try {
        const payload = { lots: newLots };
        if (newTvaDefaut !== null) payload.tva_defaut = newTvaDefaut;
        const { data } = await axios.put(`${API_URL}/api/chantiers/${chantierId}/dpgf`, payload, { withCredentials: true });
        // Cohérence DPGF↔DCE v31 — refresh banner si divergence détectée
        if (data?.has_divergences) {
          consistencyBannerRef.current?.reload();
        }
        // RP45 — lot confié modifié : il passe « À relire »
        if (data?.a_relire?.length) refreshLotsRef.current?.();
      } catch (err) {
        // Audit 2026-09 CON-05 — plus de perte silencieuse : message explicite, et sur refus
        // de droits (403) on recharge la DPGF serveur pour ne pas afficher une saisie non enregistrée.
        console.error('Autosave error:', err);
        const detail = err.response?.data?.detail;
        toast.error(`DPGF non enregistrée : ${typeof detail === 'string' ? detail : 'erreur réseau'}`, { id: 'dpgf-autosave-error' });
        if (err.response?.status === 403) fetchDPGF();
      }
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chantierId]);

  const updateLots = (newLots) => {
    if (!canEditAny) return; // Audit 2026-09 CON-05 — lecture seule réelle (RP45 : ses lots)
    setLots(newLots);
    autoSave(newLots);
  };

  const updateTvaDefaut = (newPct) => {
    if (!canEdit) return;
    const v = parseFloat(newPct);
    if (![5.5, 10, 20].includes(v)) return;
    setTvaDefaut(v);
    autoSave(lots, v);
  };

  // Iter 84 — Override TVA par lot (null = hérite tva_defaut DPGF)
  const updateLotTva = (lotIdx, newTvaPct) => {
    if (!canEditLot(lots[lotIdx]?.id)) return;
    if (newTvaPct !== null && ![5.5, 10, 20].includes(newTvaPct)) return;
    const newLots = lots.map((l, i) => i === lotIdx ? { ...l, tva_pct: newTvaPct } : l);
    setLots(newLots);
    autoSave(newLots, tvaDefaut);
  };

  // ===== SELECTION =====
  const totalPostesCount = lots.reduce((s, l) => s + (l.postes?.length || 0), 0);
  const allSelected = totalPostesCount > 0 && selectedPostes.size === totalPostesCount;

  const togglePoste = (lotIdx, posteIdx) => {
    const key = `${lotIdx}-${posteIdx}`;
    setSelectedPostes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleLot = (lotIdx) => {
    const lot = lots[lotIdx];
    if (!lot || !lot.postes) return;
    const lotKeys = lot.postes.map((_, pi) => `${lotIdx}-${pi}`);
    if (lotKeys.length === 0) return;
    const allLotSelected = lotKeys.every((k) => selectedPostes.has(k));
    setSelectedPostes((prev) => {
      const next = new Set(prev);
      lotKeys.forEach((k) => { if (allLotSelected) next.delete(k); else next.add(k); });
      return next;
    });
  };

  const isLotFullySelected = (lotIdx) => {
    const lot = lots[lotIdx];
    if (!lot || !lot.postes || lot.postes.length === 0) return false;
    return lot.postes.every((_, pi) => selectedPostes.has(`${lotIdx}-${pi}`));
  };

  const isLotPartiallySelected = (lotIdx) => {
    const lot = lots[lotIdx];
    if (!lot || !lot.postes || lot.postes.length === 0) return false;
    const selectedCount = lot.postes.filter((_, pi) => selectedPostes.has(`${lotIdx}-${pi}`)).length;
    return selectedCount > 0 && selectedCount < lot.postes.length;
  };

  const selectAll = () => {
    const all = new Set();
    lots.forEach((lot, li) => (lot.postes || []).forEach((_, pi) => all.add(`${li}-${pi}`)));
    setSelectedPostes(all);
  };
  const clearSelection = () => setSelectedPostes(new Set());

  const deleteSelected = () => {
    if (selectedPostes.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedPostes.size} poste(s) sélectionné(s) ?`)) return;
    const newLots = lots.map((lot, li) => ({
      ...lot,
      postes: (lot.postes || []).filter((_, pi) => !selectedPostes.has(`${li}-${pi}`)),
    }));
    setSelectedPostes(new Set());
    updateLots(newLots);
    toast.success('Sélection supprimée');
  };

  // PATCH UX #5 — Dupliquer la sélection
  const duplicateSelected = () => {
    if (selectedPostes.size === 0) return;
    const newLots = lots.map((lot, li) => {
      const indicesToCopy = (lot.postes || [])
        .map((_, pi) => pi)
        .filter((pi) => selectedPostes.has(`${li}-${pi}`));
      if (!indicesToCopy.length) return lot;
      const copies = indicesToCopy.map((pi) => ({
        ...lot.postes[pi],
        id: genId('p'),
      }));
      return { ...lot, postes: [...lot.postes, ...copies] };
    });
    setSelectedPostes(new Set());
    updateLots(newLots);
    toast.success(`${selectedPostes.size} poste(s) dupliqué(s)`);
  };

  // Sprint B #2 — Améliorer désignations via Claude
  const improveSelected = async () => {
    if (selectedPostes.size === 0) return;
    const posteIds = [];
    selectedPostes.forEach((key) => {
      const [li, pi] = key.split('-').map(Number);
      const p = lots[li]?.postes?.[pi];
      if (p?.id) posteIds.push(p.id);
    });
    if (!posteIds.length) return;
    const instructions = window.prompt('Instructions additionnelles pour la reformulation ? (laissez vide pour défaut)', '') || '';
    setImproving(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/ameliorer-designations`,
        { poste_ids: posteIds, instructions },
        { withCredentials: true }
      );
      toast.success('Désignations améliorées');
      setSelectedPostes(new Set());
      await fetchDPGF();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setImproving(false);
    }
  };

  // ===== CRUD lot/poste =====
  const addLot = () => {
    updateLots([...lots, { id: genId('lot'), nom: 'Nouveau lot', postes: [] }]);
  };
  const removeLot = (lotIdx) => {
    if (!window.confirm('Supprimer ce lot et tous ses postes ?')) return;
    updateLots(lots.filter((_, i) => i !== lotIdx));
    setSelectedPostes(new Set());
  };
  const updateLotName = (lotIdx, nom) => {
    const newLots = [...lots];
    newLots[lotIdx] = { ...newLots[lotIdx], nom };
    updateLots(newLots);
  };

  const addPoste = (lotIdx, categorie = null) => {
    const newLots = [...lots];
    const newPoste = {
      id: genId('p'),
      designation: '',
      unite: '',
      quantite: 0,
      prix_unitaire: 0,
      categorie: categorie || null,
    };
    newLots[lotIdx] = { ...newLots[lotIdx], postes: [...newLots[lotIdx].postes, newPoste] };
    updateLots(newLots);
  };

  const removePoste = (lotIdx, posteIdx) => {
    const newLots = [...lots];
    newLots[lotIdx] = { ...newLots[lotIdx], postes: newLots[lotIdx].postes.filter((_, i) => i !== posteIdx) };
    updateLots(newLots);
  };

  const updatePoste = (lotIdx, posteIdx, field, value) => {
    const newLots = [...lots];
    const postes = [...newLots[lotIdx].postes];
    postes[posteIdx] = { ...postes[posteIdx], [field]: value };
    newLots[lotIdx] = { ...newLots[lotIdx], postes };
    updateLots(newLots);
  };

  // Sprint B #4 — Update partial (pour patcher plusieurs champs ex: localisation)
  const updatePostePartial = (lotIdx, posteIdx, patch) => {
    const newLots = [...lots];
    const postes = [...newLots[lotIdx].postes];
    postes[posteIdx] = { ...postes[posteIdx], ...patch };
    newLots[lotIdx] = { ...newLots[lotIdx], postes };
    updateLots(newLots);
  };

  // PATCH UX #1 — Sous-catégories
  const addSubCategory = (lotIdx) => {
    const name = window.prompt('Nom de la sous-catégorie ?');
    if (!name || !name.trim()) return;
    const cleanName = name.trim();
    const newLots = [...lots];
    const newPoste = {
      id: genId('p'),
      designation: '',
      unite: '',
      quantite: 0,
      prix_unitaire: 0,
      categorie: cleanName,
    };
    newLots[lotIdx] = { ...newLots[lotIdx], postes: [...newLots[lotIdx].postes, newPoste] };
    updateLots(newLots);
  };

  const renameCategory = (lotIdx, oldName, newName) => {
    if (!newName.trim() || newName === oldName) return;
    const newLots = [...lots];
    newLots[lotIdx] = {
      ...newLots[lotIdx],
      postes: newLots[lotIdx].postes.map((p) => (
        (p.categorie || null) === (oldName === UNCATEGORIZED ? null : oldName)
          ? { ...p, categorie: newName.trim() }
          : p
      )),
    };
    updateLots(newLots);
  };

  const removeCategory = (lotIdx, catName) => {
    if (catName === UNCATEGORIZED) return;
    if (!window.confirm(`Supprimer la sous-catégorie "${catName}" et ses postes ?`)) return;
    const newLots = [...lots];
    newLots[lotIdx] = {
      ...newLots[lotIdx],
      postes: newLots[lotIdx].postes.filter((p) => (p.categorie || null) !== catName),
    };
    setSelectedPostes(new Set());
    updateLots(newLots);
  };

  const toggleCatCollapse = (key) => {
    setCollapsedCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ===== DnD =====
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const aType = active.data.current?.type;
    const oType = over.data.current?.type;

    // Réordonner LOTS
    if (aType === 'lot' && oType === 'lot') {
      const oldIdx = lots.findIndex((l) => `lot:${l.id}` === active.id);
      const newIdx = lots.findIndex((l) => `lot:${l.id}` === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(lots, oldIdx, newIdx);
      setSelectedPostes(new Set()); // numérotation change → reset sélection sûre
      updateLots(reordered);
      return;
    }

    // Réordonner POSTES (au sein d'un même lot uniquement)
    if (aType === 'poste' && oType === 'poste') {
      const aId = active.id.replace(/^poste:/, '');
      const oId = over.id.replace(/^poste:/, '');
      const lotIdx = lots.findIndex((l) => l.postes?.some((p) => p.id === aId));
      const targetLotIdx = lots.findIndex((l) => l.postes?.some((p) => p.id === oId));
      if (lotIdx === -1 || targetLotIdx === -1 || lotIdx !== targetLotIdx) return;
      const lot = lots[lotIdx];
      const oldIdx = lot.postes.findIndex((p) => p.id === aId);
      const newIdx = lot.postes.findIndex((p) => p.id === oId);
      if (oldIdx === -1 || newIdx === -1) return;
      // Si on drop sur un poste d'une autre catégorie, on aligne la catégorie cible
      const targetCat = lot.postes[newIdx].categorie || null;
      const reorderedPostes = arrayMove(lot.postes, oldIdx, newIdx)
        .map((p, idx) => (idx === newIdx ? { ...p, categorie: targetCat } : p));
      const newLots = [...lots];
      newLots[lotIdx] = { ...lot, postes: reorderedPostes };
      setSelectedPostes(new Set());
      updateLots(newLots);
    }
  };

  // ===== Génération CCTP / import / export =====
  const handleGenerateCCTP = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/dpgf/generate-cctp`, {}, { withCredentials: true });
      // Audit 2026-09 CON-04 — fusion : les textes CCTP existants sont conservés
      const ajouts = (data?.lots_ajoutes || 0) + (data?.postes_ajoutes || 0);
      toast.success(
        ajouts > 0
          ? `CCTP mis à jour : ${data.lots_ajoutes || 0} lot(s) et ${data.postes_ajoutes || 0} poste(s) ajoutés, textes existants conservés`
          : 'CCTP déjà à jour avec la DPGF (textes existants conservés)'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Audit 2026-09 CON-13 — l'ancien format .xls n'est pas lisible par le serveur
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.xlsx') && !lower.endsWith('.xlsm')) {
      toast.error(lower.endsWith('.xls')
        ? 'Ancien format .xls non supporté : enregistrez le fichier en .xlsx (Excel : Fichier > Enregistrer sous)'
        : 'Fichier .xlsx requis');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/import`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(data.message);
      fetchDPGF();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Groupes par catégorie pour rendu (préserve l'ordre des postes)
  const groupedByLot = useMemo(() => lots.map((lot) => {
    const groups = []; // [{ categorie, posteIndices: [originalPosteIdx,...] }]
    const seen = new Map(); // categorieKey -> groupIndex
    (lot.postes || []).forEach((p, posteIdx) => {
      const key = p.categorie || UNCATEGORIZED;
      if (!seen.has(key)) {
        seen.set(key, groups.length);
        groups.push({ categorie: key, posteIndices: [] });
      }
      groups[seen.get(key)].posteIndices.push(posteIdx);
    });
    return groups;
  }), [lots]);

  const totalGeneral = lots.reduce(
    (sum, lot) => sum + lot.postes.reduce((s, p) => s + ((p.quantite || 0) * (p.prix_unitaire || 0)), 0),
    0
  );

  if (loading) return <div className="space-y-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>;

  return (
    <div data-testid="dpgf-editor">
      {/* Cohérence DPGF↔DCE v31 — bandeau alerte */}
      <DpgfDceConsistencyBanner
        ref={consistencyBannerRef}
        chantierId={chantierId}
        onActionComplete={() => fetchDPGF()}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="rounded-none gap-2 text-[#6B6B6B] hover:text-[#2C2C2C]" onClick={onBack} data-testid="dpgf-back">
            <ArrowLeft size={18} /> Retour
          </Button>
          <div>
            <h2 className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
              DPGF — {chantierName}
            </h2>
            <p className="text-xs text-[#6B6B6B]">Décomposition du Prix Global et Forfaitaire</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm" onChange={handleImportExcel} className="hidden" />
          <Button
            variant="outline" className="rounded-none gap-1 text-xs h-8"
            onClick={allSelected ? clearSelection : selectAll}
            disabled={totalPostesCount === 0}
            data-testid="dpgf-select-all"
          >
            {allSelected ? <CheckSquare size={14} weight="fill" /> : <Square size={14} />}
            {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </Button>
          {/* Fluidité B2 (P2#17) — « Générer … depuis docs » et « Importer Excel » : une seule
              entrée « Importer » qui demande quoi importer (mêmes fonctions qu'avant). */}
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => setImporterOpen(true)} disabled={importing || !canEdit} title={editTitle} data-testid="dpgf-importer">
            <UploadSimple size={14} /> {importing ? 'Import...' : BOUTONS.importer}
          </Button>
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8 text-[#1E3A5F] border-[#1E3A5F]" onClick={handleGenerateCCTP} disabled={!canEdit} title={editTitle} data-testid="dpgf-generate-cctp">
            <Rows size={14} /> Générer le CCTP
          </Button>
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/dpgf/export-xlsx`, '_blank')} data-testid="dpgf-export-excel">
            <Export size={14} /> Excel
          </Button>
          <Button variant="outline" className="rounded-none gap-1 text-xs h-8" onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/dpgf/export-pdf`, '_blank')} data-testid="dpgf-export-pdf">
            <FilePdf size={14} /> PDF
          </Button>
          <div className="flex items-center gap-1 border border-[#E0E0DB] px-2 h-8" title="TVA par défaut appliquée à tous les lots (modifiable lot par lot). Modifiable même après validation du DPGF (donnée fiscale)." data-testid="dpgf-tva-defaut-block">
            <span className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">TVA</span>
            <select
              value={tvaDefaut}
              onChange={(e) => updateTvaDefaut(e.target.value)}
              disabled={!canEdit}
              className="bg-transparent text-xs font-semibold text-[#2C2C2C] outline-none cursor-pointer disabled:cursor-not-allowed disabled:text-[#9A9A9A]"
              data-testid="dpgf-tva-defaut-select"
            >
              <option value={5.5}>5,5%</option>
              <option value={10}>10%</option>
              <option value={20}>20%</option>
            </select>
            {dpgfStatus === 'VALIDEE' && (
              <span className="text-[10px] text-amber-600 ml-1" title="DPGF validé — modifier la TVA met aussi à jour les DCE actifs">⚠</span>
            )}
          </div>
          <Button
            variant="outline"
            className="rounded-none gap-1 text-xs h-8 text-[#1E3A5F] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
            onClick={() => setShowVersionsModal(true)}
            data-testid="dpgf-versions-btn"
          >
            <ClockCounterClockwise size={14} /> Versions
          </Button>
        </div>
      </div>

      {/* AI Banner */}
      <DPGFProactiveBanner chantierId={chantierId} dpgfLots={lots} onImport={fetchDPGF} />

      {/* Sprint C — Bandeau commentaires AMO non-résolus (visible archi/MOE) */}
      {amoComments.length > 0 && (
        <div
          className="border-l-4 border-[#7B5EA7] bg-[#F5F0FB] p-3 mb-4 text-sm"
          data-testid="dpgf-amo-comments-banner"
        >
          <div className="flex items-center gap-2 mb-2">
            <ChatCircleDots size={16} className="text-[#7B5EA7]" weight="fill" />
            <span className="font-bold text-[#4B3A6B]">
              {amoComments.length} commentaire{amoComments.length > 1 ? 's' : ''} AMO en attente
            </span>
          </div>
          <ul className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {amoComments.slice(0, 8).map((c) => (
              <li
                key={c.id}
                className="flex items-start gap-2 text-xs bg-white border border-[#E5DDF0] p-2"
                data-testid={`dpgf-amo-comment-${c.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2C2C]">
                    {c.auteur_nom}
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-[#7B5EA7] font-bold">
                      {c.type === 'demande_ajout' ? 'Demande d\'ajout' : 'Commentaire'}
                    </span>
                  </div>
                  {c.designation_proposee && (
                    <div className="italic text-[#4B3A6B]">→ {c.designation_proposee}</div>
                  )}
                  <div className="text-[#6B6B6B] mt-0.5 line-clamp-2">{c.commentaire}</div>
                </div>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-[10px] h-6 rounded-none text-[#2D6A4F] hover:bg-[#E8F3EE]"
                    onClick={() => markAmoCommentResolu(c.id)}
                    data-testid={`dpgf-amo-comment-resolu-${c.id}`}
                  >
                    Marquer traité
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Table */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="border border-[#E0E0DB] bg-white">
          {/* Table Header */}
          <div className="grid grid-cols-[24px_32px_40px_1fr_80px_90px_120px_120px_36px] bg-[#2C2C2C] text-white text-xs font-bold uppercase tracking-wider">
            <div className="p-2"></div>
            <div className="p-2 flex items-center justify-center">
              <input
                type="checkbox" checked={allSelected}
                onChange={() => (allSelected ? clearSelection() : selectAll())}
                className="h-3 w-3 accent-[#C75B39] cursor-pointer"
                data-testid="dpgf-header-checkbox"
              />
            </div>
            <div className="p-2">N°</div>
            <div className="p-2">Désignation</div>
            <div className="p-2">Unité</div>
            <div className="p-2 text-right">Qté</div>
            <div className="p-2 text-right">PU HT (€)</div>
            <div className="p-2 text-right">Total HT (€)</div>
            <div className="p-2"></div>
          </div>

          <SortableContext items={lots.map((l) => `lot:${l.id}`)} strategy={verticalListSortingStrategy}>
            {lots.map((lot, lotIdx) => {
              const lotTotal = lot.postes.reduce((s, p) => s + ((p.quantite || 0) * (p.prix_unitaire || 0)), 0);
              const groups = groupedByLot[lotIdx];
              const lotEditable = canEditLot(lot.id); // RP45 — lots confiés
              return (
                <SortableLot
                  key={lot.id} lot={lot} lotIdx={lotIdx}
                  onRemove={() => removeLot(lotIdx)}
                  onRename={(name) => updateLotName(lotIdx, name)}
                  isFullySelected={isLotFullySelected(lotIdx)}
                  isPartiallySelected={isLotPartiallySelected(lotIdx)}
                  onToggleAll={() => toggleLot(lotIdx)}
                  lotTotal={lotTotal}
                  canEdit={lotEditable}
                  canEditStructure={canEdit}
                  onNavigateCCTP={onNavigateCCTP}
                  onUpdateLotTva={updateLotTva}
                  tvaDefaut={tvaDefaut}
                >
                  {/* RP44-46 — lot confié : à qui, statut, signaler / valider */}
                  <LotConfieBar chantierId={chantierId} lotId={lot.id} lotNom={lot.nom} lotsConfies={lotsConfies} />
                  {/* Sous-catégories */}
                  <SortableContext
                    items={(lot.postes || []).map((p) => `poste:${p.id}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {groups.map((group) => {
                      const cat = group.categorie;
                      const collapseKey = `${lotIdx}::${cat}`;
                      const isCollapsed = collapsedCats.has(collapseKey);
                      const subTotal = group.posteIndices.reduce(
                        (s, pi) => s + ((lot.postes[pi].quantite || 0) * (lot.postes[pi].prix_unitaire || 0)), 0
                      );
                      const showHeader = cat !== UNCATEGORIZED || groups.length > 1;
                      return (
                        <div key={collapseKey} data-testid={`dpgf-cat-group-${lot.id}-${cat}`}>
                          {showHeader && cat !== UNCATEGORIZED && (
                            <div className="grid grid-cols-[24px_32px_40px_1fr_80px_90px_120px_120px_36px] bg-[#FFF3E8] border-b border-[#E0E0DB] items-center group" data-testid={`dpgf-cat-header-${lot.id}-${cat}`}>
                              <div className="p-2"></div>
                              <button onClick={() => toggleCatCollapse(collapseKey)} className="p-2 text-[#C75B39] hover:text-[#A14828]" title={isCollapsed ? 'Déplier' : 'Replier'} data-testid={`dpgf-cat-toggle-${lot.id}-${cat}`}>
                                {isCollapsed ? <CaretRight size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                              </button>
                              <div className="p-2 text-xs text-[#C75B39] font-bold">▸</div>
                              <div className="px-2 py-1 col-span-3">
                                <input
                                  defaultValue={cat}
                                  readOnly={!lotEditable}
                                  onBlur={(e) => { if (lotEditable) renameCategory(lotIdx, cat, e.target.value); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                  className="bg-transparent text-sm font-bold text-[#C75B39] w-full outline-none"
                                  placeholder="Nom de la sous-catégorie"
                                  data-testid={`dpgf-cat-input-${lot.id}-${cat}`}
                                />
                              </div>
                              <div className="p-2 text-right text-xs text-[#C75B39] font-medium">
                                {group.posteIndices.length} poste{group.posteIndices.length > 1 ? 's' : ''}
                              </div>
                              <div className="p-2 text-right text-sm font-bold text-[#C75B39]">{subTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</div>
                              <div className="p-1 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                {lotEditable && (
                                  <button onClick={() => removeCategory(lotIdx, cat)} className="text-[#9A9A9A] hover:text-[#8B3A3A]" title="Supprimer la sous-catégorie" data-testid={`dpgf-cat-remove-${lot.id}-${cat}`}>
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {!isCollapsed && group.posteIndices.map((posteIdx) => {
                            const poste = lot.postes[posteIdx];
                            const posteKey = `${lotIdx}-${posteIdx}`;
                            return (
                              <SortablePoste
                                key={poste.id} poste={poste}
                                lotId={lot.id} lotName={lot.nom}
                                lotNum={String(lotIdx + 1).padStart(2, '0')}
                                posteNum={posteIdx + 1}
                                isSelected={selectedPostes.has(posteKey)}
                                canEdit={lotEditable}
                                canEditCctp={canEditCctp}
                                chantierId={chantierId}
                                nbTaches={tasksByPoste[poste.id] || 0}
                                onCommentAmo={canCommentAmo ? () => setAmoCommentBox({
                                  posteId: poste.id,
                                  posteDesignation: poste.designation,
                                  lotId: lot.id,
                                  lotNom: lot.nom,
                                  defaultType: 'comment',
                                }) : null}
                                onToggle={() => togglePoste(lotIdx, posteIdx)}
                                onUpdate={(field, value) => updatePoste(lotIdx, posteIdx, field, value)}
                                onDelete={() => removePoste(lotIdx, posteIdx)}
                                onOpenLocation={() => setLocationFor({ lotIdx, posteIdx })}
                              />
                            );
                          })}
                          {lotEditable && !isCollapsed && cat !== UNCATEGORIZED && (
                            <div className="border-b border-[#E0E0DB] py-1 px-2 bg-[#FFFCF8]">
                              <button onClick={() => addPoste(lotIdx, cat)} className="text-xs text-[#C75B39] hover:text-[#D97B5C] flex items-center gap-1 py-1" data-testid={`add-poste-cat-${lot.id}-${cat}`}>
                                <Plus size={12} weight="bold" /> Ajouter un poste à « {cat} »
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </SortableContext>

                  {/* Footer du lot : ajouter poste / sous-catégorie */}
                  {lotEditable && <div className="border-b border-[#E0E0DB] py-1 px-2 flex items-center gap-4">
                    <button onClick={() => addPoste(lotIdx, null)} className="text-xs text-[#C75B39] hover:text-[#D97B5C] flex items-center gap-1 py-1" data-testid={`add-poste-lot-${lot.id}`}>
                      <Plus size={12} weight="bold" /> Ajouter un poste
                    </button>
                    <button onClick={() => addSubCategory(lotIdx)} className="text-xs text-[#1E3A5F] hover:text-[#15293F] flex items-center gap-1 py-1" data-testid={`add-subcat-${lot.id}`}>
                      <FolderPlus size={12} weight="bold" /> Sous-catégorie
                    </button>
                  </div>}
                </SortableLot>
              );
            })}
          </SortableContext>

          {/* Total General */}
          <div className="grid grid-cols-[24px_32px_40px_1fr_80px_90px_120px_120px_36px] bg-[#F5F5F3] border-t-2 border-[#2C2C2C] items-center">
            <div className="p-2"></div>
            <div className="p-2"></div>
            <div className="p-2"></div>
            <div className="p-2 text-sm font-bold text-[#2C2C2C] col-span-4">TOTAL GÉNÉRAL HT</div>
            <div className="p-2 text-right text-base font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }} data-testid="dpgf-total-general">
              {totalGeneral.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
            </div>
            <div className="p-2"></div>
          </div>
        </div>
      </DndContext>

      {/* Add lot button */}
      {canEdit && (
        <div className="mt-3">
          <Button variant="outline" className="rounded-none gap-2 text-sm border-dashed border-[#C75B39] text-[#C75B39] hover:bg-[#FFF3E0]" onClick={addLot} data-testid="add-lot-button">
            <Plus size={16} weight="bold" /> Ajouter un lot
          </Button>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs text-[#9A9A9A]" data-testid="dpgf-save-status">
        <FloppyDisk size={14} /> {canEdit
          ? 'Sauvegarde automatique activée · Glisser pour réordonner'
          : canEditAny
            ? 'Sauvegarde automatique de vos lots · les autres lots restent en lecture'
            : editTitle}
      </div>

      <ImporterDialog
        open={importerOpen}
        onOpenChange={setImporterOpen}
        chantierId={chantierId}
        choix={['dpgfExcel', 'dpgfDocuments']}
        droits={{ dpgfExcel: canEdit, dpgfDocuments: canEdit }}
        gestionnaires={{ dpgfExcel: () => fileInputRef.current?.click(), dpgfDocuments: () => setAiDialogOpen(true) }}
      />
      <DPGFGenerateAIDialog
        open={aiDialogOpen}
        onOpenChange={setAiDialogOpen}
        chantierId={chantierId}
        onImported={fetchDPGF}
      />

      {/* Sprint B #4 — Modale rattachement localisation */}
      {locationFor && (
        <DPGFLocationPopover
          open={!!locationFor}
          onClose={() => setLocationFor(null)}
          poste={lots[locationFor.lotIdx]?.postes?.[locationFor.posteIdx]}
          chantierId={chantierId}
          onSave={(patch) => updatePostePartial(locationFor.lotIdx, locationFor.posteIdx, patch)}
        />
      )}

      {/* Sprint B #3 — Section Avenants (visible si DPGF verrouillée OU avenants existants) */}
      <DPGFAvenantsManager chantierId={chantierId} dpgfStatus={dpgfStatus} />

      {/* Sprint C — Modale commentaire AMO */}
      {amoCommentBox && (
        <AmoCommentBox
          open={!!amoCommentBox}
          onClose={() => setAmoCommentBox(null)}
          chantierId={chantierId}
          posteId={amoCommentBox.posteId}
          posteDesignation={amoCommentBox.posteDesignation}
          lotId={amoCommentBox.lotId}
          lotNom={amoCommentBox.lotNom}
          defaultType={amoCommentBox.defaultType}
          onCreated={() => fetchAmoComments()}
        />
      )}

      {/* Sprint D — Modale historique versions */}
      <DPGFVersionsModal
        open={showVersionsModal}
        onClose={() => setShowVersionsModal(false)}
        chantierId={chantierId}
        canRestore={canEdit}
        onRestored={() => { fetchDPGF(); fetchTasksCount(); }}
      />

      {/* Selection bar */}
      {selectedPostes.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#2C2C2C] text-white shadow-2xl border border-[#C75B39]/40 px-4 py-3 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4"
          data-testid="dpgf-selection-bar"
        >
          <span className="text-sm font-medium">
            <span className="text-[#C75B39] font-bold" data-testid="dpgf-selected-count">{selectedPostes.size}</span>{' '}
            {selectedPostes.size > 1 ? 'éléments sélectionnés' : 'élément sélectionné'}
          </span>
          <div className="h-5 w-px bg-white/20" />
          {canEdit && <button
            onClick={duplicateSelected}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A5F] hover:bg-[#152c46] text-white text-xs font-semibold transition-colors"
            data-testid="dpgf-duplicate-selection"
          >
            <Copy size={14} weight="bold" /> Dupliquer
          </button>}
          {canEdit && <button
            onClick={improveSelected}
            disabled={improving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#6366F1] hover:bg-[#4F50DC] text-white text-xs font-semibold transition-colors disabled:opacity-50"
            data-testid="dpgf-improve-selection"
          >
            <MagicWand size={14} weight="fill" /> {improving ? 'Reformulation IA…' : 'Améliorer (IA)'}
          </button>}
          {canEdit && <button
            onClick={deleteSelected}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#8B3A3A] hover:bg-[#A04A4A] text-white text-xs font-semibold transition-colors"
            data-testid="dpgf-delete-selection"
          >
            <Trash size={14} weight="bold" /> Supprimer
          </button>}
          <button
            onClick={clearSelection}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/30 hover:bg-white/10 text-white text-xs transition-colors"
            data-testid="dpgf-clear-selection"
          >
            Tout désélectionner
          </button>
        </div>
      )}
    </div>
  );
}
